import React, { Fragment, useState, useRef, useLayoutEffect } from "react";
import { ReactSVG } from "react-svg";
import { Menu, Transition } from "@headlessui/react";
import { EyeSlashIcon } from "@heroicons/react/24/outline";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import { useDispatch } from "react-redux";
import { deleteClinicalAlerts, updateClinicalAlert } from "../../../../../redux/modules/patient/actions";
import NewToolTip from "../../../../common/New-ToolTip";
import { capitalizeFirstLetter, formatDiagnosisDateForDisplay, formatWordsIntoLines } from "../../../../../helpers/functions";
import { useClickOutsideTooltip } from "../../../../../hooks/useClickOutsideTooltip";

const ClinicalAlertNameCell = ({ nameText, tooltipKey }) => {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const tipId = `alert-name-tooltip-${tooltipKey}`;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !nameText) {
      setTruncated(false);
      return undefined;
    }
    const sync = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nameText]);

  return (
    <div className="truncate">
      <div
        ref={ref}
        className="truncate"
        {...(truncated ? { "data-tooltip-id": tipId } : {})}
      >
        {nameText}
      </div>
      {truncated && (
        <NewToolTip
          id={tipId}
          render={() => (
            <div className="bg-gray-10 p-2 text-teal-300 text-body-sm max-w-[300px]">
              {nameText}
            </div>
          )}
        />
      )}
    </div>
  );
};

const ClinicalAlertTable = ({
  clinicalAlerts = [],
  tableFilter,
  setTableFilter,
  setShowCreateSlider,
  setSelectedAlert,
  selectedAlert,
  setCreate,
  onDeleted = () => {},
  onUpdated = () => {},
  showTitle = true,
  borderless = false,
}) => {
  const dispatch = useDispatch();
  const [idToDelete, setIdToDelete] = useState(null);
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false);
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const tooltipWrapperRef = useClickOutsideTooltip(openTooltipId, setOpenTooltipId);

  // Keep this table simple like Allergy table; slider will handle normalization

  const deleteAlert = async (alertId) => {
    try {
      await dispatch(deleteClinicalAlerts([alertId]));
      setOpenDeleteAlert(false);
      setIdToDelete(null);
      onDeleted();
    } catch (e) {
      // no-op: toast handled in action
    }
  };

  const getAlertNameBasedOnId = (id) => {
    if (id !== null) {
      return clinicalAlerts.find((val) => val.id === id)?.alertType ?? "";
    } else {
      return "";
    }
  };

  // Render comments tooltip content (aligned with Medical History tooltip)
  const renderCommentsTooltip = (comment) => (
    !comment ? null : (
      <div className="bg-white max-w-[460px]">
        <div className="flex flex-col gap-3 p-2">
          <div className="text-teal-300 text-sm-body prose-a:underline">
            <div className="flex flex-col gap-4">
              <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {formatWordsIntoLines(comment)}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  );

  // Normalize view status from various backend/front-end shapes
  const normalizeViewStatus = (alert) => {
    const raw = alert?.viewStatus?.value
      ? alert.viewStatus.value
      : (alert?.viewStatus || alert?.ViewType || alert?.viewType || "");
    // If raw is null/empty, treat as explicit N/A (no view status selected)
    if (raw === null || raw === undefined || String(raw).trim() === "") return "na";
    const s = String(raw).toLowerCase();
    return s.includes("pharmacist") ? "pharmacist-view" : "always-visible";
  };

  // Detect alerts explicitly marked as 'None' so UI shows N/A
  const isNoneAlert = (alert) => {
    const t = alert?.alertType || alert?.clinicalAlertType || alert?.ClinicalAlertType || "";
    return String(t).trim().toLowerCase() === "none";
  };

  // Toggle view status when eye icon is clicked
  const handleToggleViewStatus = async (alert) => {
    const currentViewStatus = normalizeViewStatus(alert);
    const newViewStatus = currentViewStatus === "pharmacist-view" 
      ? "Always Visible" 
      : "Pharmacist View";

    // Prepare the alert data for update
    const alertToUpdate = {
      id: alert.id,
      alertType: alert.alertType || alert.clinicalAlertType || alert.ClinicalAlertType,
      viewStatus: newViewStatus,
      initiatedBy: alert.initiatedBy || alert.InitiatedBy || "",
      comments: alert.comments || alert.Comments || "",
      date: alert.date || alert.clinicalAlertDate || alert.ClinicalAlertDate || alert.createdAt,
      diagnosisDate: alert.diagnosisDate !== undefined 
        ? alert.diagnosisDate 
        : (alert.DiagnosisDate !== undefined ? alert.DiagnosisDate : null),
    };

    await dispatch(updateClinicalAlert(alert.id, alertToUpdate));
    onUpdated();
  };

  // Filter alerts based on tableFilter
  const filteredAlerts = (clinicalAlerts || []).filter((alert) => {
    const normalized = normalizeViewStatus(alert);
    if (tableFilter === "always-visible") return normalized === "always-visible";
    if (tableFilter === "pharmacist-view") return normalized === "pharmacist-view";
    return true; // 'all'
  });

  return (
    <div className={`${borderless ? "bg-white p-0" : "rounded-[6px] bg-white border border-gray-100 p-3"}`}>
      {showTitle && (
        <h4 className="text-[22px] font-bold text-gray-800 pb-2 pl-2">Clinical Alert Record</h4>
      )}
      {/* Table */}
      <div className={`${borderless ? "" : "rounded-[4px] border border-gray-100"}`}>
        <div className={`w-full ${filteredAlerts.length > 6 ? "overflow-y-auto scrollbar-hide max-h-[290px]" : ""}`}>
          <table className="min-w-full divide-y bg-gray-20">
          <thead className="bg-gray-20">
            <tr>
              <th
                scope="col"
                className="pl-6 py-4 text-body-md font-semibold text-left text-gray-800"
              >
                Clinical Alerts
              </th>
              <th
                scope="col"
                className="border-l border-r w-[40px] border-gray-50 py-4 text-body-md font-semibold text-left text-gray-800"
                
              >
              </th>
              <th
                scope="col"
                className="py-4 pl-[10px] text-body-md font-semibold text-left text-gray-800"
              >
                View Status
              </th>
              <th
                scope="col"
                className="py-4 text-body-md font-semibold text-left text-gray-800 hidden sm:table-cell"
              >
                Initiated By
              </th>
              <th
                scope="col"
                className="py-4 text-body-md font-semibold text-left text-gray-800 hidden sm:table-cell"
              >
                Diagnosis Date
              </th>
              <th
                scope="col"
                className="py-4 text-body-md font-semibold text-center text-gray-800 hidden sm:table-cell"
              >
                Comments
              </th>
              <th
                scope="col"
                className="pr-6 py-4 text-body-md font-semibold text-center text-gray-800"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filteredAlerts.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  className="text-center py-4 font-medium text-gray-900"
                >
                  No Clinical Alerts Found
                </td>
              </tr>
            ) : (
              filteredAlerts.map((alert, i) => {
                const viewStatus = normalizeViewStatus(alert);
                const alertTypeText = alert.alertType || alert.clinicalAlertType || alert.ClinicalAlertType || "";
                const isNone = String(alertTypeText).trim().toLowerCase() === "none";
                const isStripedRow = i % 2 === 1;
                const rowBgClass = alert.status === "inactive" ? "bg-red-50" : isStripedRow ? "bg-gray-10" : "bg-white";
                return (
                <tr
                  key={alert?.id ?? i}
                  className={`${rowBgClass} transition-colors duration-300 cursor-pointer group hover:bg-blue-50 border-b border-gray-50`}
                >
                  {/* Clinical Alert Name */}
                  <td className="pl-6 py-4 text-body-md font-medium text-teal-300 whitespace-nowrap max-w-[160px]">
                    {viewStatus === 'pharmacist-view' ? null : (
                      <ClinicalAlertNameCell
                        nameText={
                          alert.alertType ||
                          alert.clinicalAlertType ||
                          alert.ClinicalAlertType ||
                          ""
                        }
                        tooltipKey={alert?.id ?? i}
                      />
                    )}
                  </td>

                  {/* Eye Icon Column */}
                  <td className="border-l border-r border-gray-50 align-middle">
                    <div
                      className="flex items-center justify-center h-full min-h-[54px] cursor-pointer hover:opacity-70 transition-opacity"
                      data-tooltip-id={`alert-eye-tooltip-${i}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (alert.status !== "inactive") {
                          handleToggleViewStatus(alert);
                        }
                      }}
                    >
                      {viewStatus === 'pharmacist-view' ? (
                        <EyeSlashIcon className="w-5 text-gray-600" />
                      ) : (
                        <ReactSVG src="/icons/showeyeicon.svg" className="w-5 h-5" />
                      )}
                    </div>
                    <NewToolTip
                      id={`alert-eye-tooltip-${i}`}
                      place="bottom-start"
                      offset={-8}
                      render={() => (
                        <div className="bg-gray-10 p-2 text-left text-teal-300 text-body-sm max-w-[250px] whitespace-nowrap">
                          {viewStatus === "pharmacist-view"
                            ? "Click for Always Visible"
                            : "Click for Pharmacist View Only"}
                        </div>
                      )}
                    />
                  </td>

                  {/* View Status */}
                  <td className="py-4 pl-2.5 text-body-md text-gray-800 whitespace-nowrap">
                    <span>{(isNone || viewStatus === 'na') ? 'None' : (viewStatus === 'pharmacist-view' ? 'Pharmacist View' : 'Always Visible')}</span>
                  </td>

                  {/* Initiated By */}
                  <td className="py-4 text-body-md text-gray-800 whitespace-nowrap capitalize hidden sm:table-cell">
                    {isNone ? 'None' : (alert.initiatedBy || alert.InitiatedBy || 'None')}
                  </td>

                  {/* Diagnosis Date - Show only if not pharmacist-view */}
                  <td className="py-4 text-body-md text-gray-800 whitespace-nowrap hidden sm:table-cell">
                    {viewStatus === 'pharmacist-view' ? null : (() => {
                      // Priority: diagnosisDate > DiagnosisDate > clinicalAlertDate (for backward compatibility)
                      const diagnosisDateValue = alert.diagnosisDate !== undefined 
                        ? alert.diagnosisDate 
                        : (alert.DiagnosisDate !== undefined 
                          ? alert.DiagnosisDate 
                          : (alert.clinicalAlertDate || alert.ClinicalAlertDate || null));
                      
                      // Get the date option value
                      const dateOptionValue = alert.clinicalAlertDateOption !== undefined
                        ? alert.clinicalAlertDateOption
                        : (alert.ClinicalAlertDateOption || null);
                      
                      return formatDiagnosisDateForDisplay(diagnosisDateValue, dateOptionValue);
                    })()}
                  </td>

                  {/* Comments - Show only if not pharmacist-view */}
                  <td className="py-4 justify-center text-body-md text-gray-800 whitespace-nowrap hidden sm:table-cell">
                    {viewStatus !== 'pharmacist-view' && alert.comments && alert.comments.length > 0 && (
                      <div className="flex justify-center">
                        <div
                          ref={openTooltipId === `alert-comments-${i}` ? tooltipWrapperRef : null}
                          className="relative inline-flex cursor-pointer"
                          data-tooltip-id={`alert-comments-hover-${i}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const tooltipId = `alert-comments-${i}`;
                            setOpenTooltipId(
                              openTooltipId === tooltipId ? null : tooltipId
                            );
                          }}
                        >
                          <ReactSVG
                            src="/icons/paper-clip-new.svg"
                            className="w-5 h-5"
                          />
                          {openTooltipId !== `alert-comments-${i}` && (
                            <NewToolTip
                              id={`alert-comments-hover-${i}`}
                              render={() => (
                                <div className="bg-white p-2 text-teal-300 text-body-sm">
                                  Click to view comments
                                </div>
                              )}
                            />
                          )}
                          <NewToolTip
                            id={`alert-comments-${i}`}
                            isOpen={openTooltipId === `alert-comments-${i}`}
                            render={() => renderCommentsTooltip(alert.comments)}
                            clickable={true}
                            handleHide={() => setOpenTooltipId(null)}
                            anchorSelect={`[data-tooltip-id="alert-comments-hover-${i}"]`}
                          />
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="pr-6 py-4 text-body-md text-gray-800 whitespace-nowrap">
                    {alert.status !== "inactive" && (
                      <div className="flex justify-center">
                        <Menu as="div" className="relative inline-block text-left">
                          {({ open }) => {
                            // Update state when menu opens/closes
                            if (open && openMenuIndex !== i) {
                              setOpenMenuIndex(i);
                            } else if (!open && openMenuIndex === i) {
                              setOpenMenuIndex(null);
                            }
                            
                            // Hide icon if it's directly below an open menu
                            const shouldHideIcon = openMenuIndex === i - 1;
                            
                            return (
                              <>
                                <div>
                                  <Menu.Button>
                                    <ReactSVG
                                      src="/icons/triple-dot.svg"
                                      className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4] ${shouldHideIcon ? 'opacity-0 pointer-events-none' : ''}`}
                                    />
                                  </Menu.Button>
                                </div>
                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                                  enterTo="transform translate-y-0 opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                                >
                                  <Menu.Items
                                    className={
                                      i === filteredAlerts.length - 1 || filteredAlerts.length === 1
                                        ? "absolute left-[-6px] min-w-[70px] w-auto origin-bottom bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bottom-full mb-0"
                                        : "absolute left-[-6px] min-w-[70px] w-auto origin-top bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-full mt-0"
                                    }
                                  >
                                    <Menu.Item>
                                      <button
                                        className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center gap-2 hover:bg-gray-50"
                                        onClick={(e) => {
                                          const normalizedAlert = {
                                            ...alert,
                                            id: alert.id,
                                            alertType: alert.alertType || alert.clinicalAlertType || alert.ClinicalAlertType,
                                            viewStatus: alert.viewStatus || alert.viewType || alert.ViewType,
                                            initiatedBy: alert.initiatedBy || alert.InitiatedBy,
                                            comments: alert.comments || alert.Comments,
                                            date: alert.date || alert.clinicalAlertDate || alert.ClinicalAlertDate || alert.createdAt,
                                          };
                                          setSelectedAlert(normalizedAlert);
                                          setCreate(false);
                                          setShowCreateSlider(true);
                                        }}
                                      >
                                        Edit
                                      </button>
                                    </Menu.Item>
                                    <Menu.Item>
                                      <button
                                        className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center gap-2 hover:bg-gray-50"
                                        onClick={() => {
                                          setOpenDeleteAlert(true);
                                          setIdToDelete(alert.id);
                                          setSelectedAlert(alert);
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </Menu.Item>
                                  </Menu.Items>
                                </Transition>
                              </>
                            );
                          }}
                        </Menu>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModalNew
        type="primary"
        title="Delete Clinical Alert"
        content={() => {
          const name = (selectedAlert && (selectedAlert.alertType || selectedAlert.clinicalAlertType || selectedAlert.ClinicalAlertType)) || getAlertNameBasedOnId(idToDelete) || "clinical alert";
          return `Are you sure you wish to delete ${capitalizeFirstLetter(name)}?`;
        }}
        className="w-[40%]"
        confirmText="Delete"
        cancelText="Cancel"
        confirmAction={() => {
          if (idToDelete) {
            deleteAlert(idToDelete);
          }
        }}
        open={openDeleteAlert}
        handleClose={() => {
          setOpenDeleteAlert(false);
          setIdToDelete(null);
        }}
        onCross={() => {
          setOpenDeleteAlert(false);
          setIdToDelete(null);
        }}
      />
    </div>
  );
};

export default ClinicalAlertTable;

